import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { FALLBACK_CURRENCIES } from '@/hooks/useCurrenciesData';

const CURRENCY_SYMBOL_MAP: Record<string, string> = Object.fromEntries(
  FALLBACK_CURRENCIES.filter((c): c is typeof c & { symbol: string } => c.symbol != null).map((c) => [
    c.code,
    c.symbol,
  ]),
);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Monday of the week containing the given date, in local time. */
export function startOfWeekMonday(date: Date): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const daysSinceMonday = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - daysSinceMonday);
  return result;
}

function toWeekStart(weekStartDate: string | Date): Date {
  return weekStartDate instanceof Date ? new Date(weekStartDate) : parseLocalDate(weekStartDate.split('T')[0]);
}

const WEEK_LABEL_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/** Compact week label based on the Sunday ending the week, e.g. "w/e Jul 5". */
export function formatWeekEndingLabel(weekStartDate: string | Date): string {
  const end = toWeekStart(weekStartDate);
  end.setDate(end.getDate() + 6);
  return `w/e ${end.toLocaleDateString(undefined, WEEK_LABEL_FORMAT)}`;
}

/** Full week label spanning Monday to Sunday, e.g. "Jun 29 – Jul 5". */
export function formatWeekRangeLabel(weekStartDate: string | Date): string {
  const start = toWeekStart(weekStartDate);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString(undefined, WEEK_LABEL_FORMAT)} – ${end.toLocaleDateString(undefined, WEEK_LABEL_FORMAT)}`;
}

/** Ensures a datetime string is interpreted as UTC by appending 'Z' if no timezone indicator present */
function ensureUtcString(dateString: string): string {
  if (!dateString.includes('T')) return dateString;
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !/[-+]\d{2}:\d{2}$/.exec(dateString)) {
    return dateString + 'Z';
  }
  return dateString;
}

export function formatDate(dateInput: string | Date | number | null | undefined): string {
  if (!dateInput) return '-';
  const date = dateInput instanceof Date ? dateInput : new Date(ensureUtcString(String(dateInput)));
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateDDMMYYYY(dateInput: string | Date): string {
  const date = dateInput instanceof Date ? dateInput : new Date(ensureUtcString(dateInput));
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatWholeNumber(value: number): string {
  // Safety check for invalid values
  if (value === null || value === undefined || isNaN(value)) {
    value = 0;
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatCurrency(value: number, currency: string): string {
  // Safety check for invalid values
  if (value === null || value === undefined || isNaN(value)) {
    value = 0;
  }

  const symbol = CURRENCY_SYMBOL_MAP[currency] || `${currency} `;
  return `${symbol}${formatWholeNumber(value)}`;
}

export function getCurrencySymbol(currencyCode: string): string {
  return CURRENCY_SYMBOL_MAP[currencyCode] || `${currencyCode} `;
}

export function formatCreditLimit(value: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL_MAP[currency] || `${currency} `;
  const roundedValue = Math.round(value);
  return `${symbol}${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(roundedValue)}`;
}

export function formatTime(dateInput: string | Date | number, options?: { hour12?: boolean }): string {
  const date =
    dateInput instanceof Date
      ? dateInput
      : typeof dateInput === 'number'
        ? new Date(dateInput)
        : new Date(ensureUtcString(dateInput));
  const localeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  if (options?.hour12 !== undefined) {
    localeOptions.hour12 = options.hour12;
  }
  return date.toLocaleTimeString(undefined, localeOptions);
}

export function formatDateTime(dateInput: string | Date, options?: { hour12?: boolean }): string {
  const date = dateInput instanceof Date ? dateInput : new Date(ensureUtcString(String(dateInput)));
  const localeOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  if (options?.hour12 !== undefined) {
    localeOptions.hour12 = options.hour12;
  }
  return date.toLocaleString(undefined, localeOptions);
}
